import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Step5CoverLetter = ({ wizardData, updateWizardData, nextStep, prevStep }) => {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(!!wizardData.coverLetter);
  const [applicantName, setApplicantName] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-cover-letter`, {
        job_description: wizardData.jobDescription,
        tailored_resume: wizardData.tailoredResume,
        applicant_name: applicantName
      });

      updateWizardData({ coverLetter: response.data.cover_letter_text });
      setGenerated(true);
      toast.success('Cover letter generated!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate cover letter');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
          Cover Letter
        </h2>
        <p className="text-lg text-muted-foreground">
          AI-generated cover letter matching your tailored resume
        </p>
      </div>

      {!generated && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="applicant-name" className="text-base font-semibold mb-2">
              Your Name (Optional)
            </Label>
            <Input
              id="applicant-name"
              data-testid="applicant-name-input"
              placeholder="Enter your name"
              value={applicantName}
              onChange={(e) => setApplicantName(e.target.value)}
              className="h-12"
            />
          </div>

          <Button
            data-testid="generate-cover-letter-btn"
            size="lg"
            onClick={handleGenerate}
            disabled={generating}
            className="w-full h-14 text-lg font-semibold transition-all active:scale-95"
          >
            {generating ? (
              <>
                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 w-5 h-5" />
                Generate Cover Letter
              </>
            )}
          </Button>
        </div>
      )}

      {generated && wizardData.coverLetter && (
        <Card className="p-8 bg-white text-slate-900 shadow-lg">
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">Your Cover Letter</h3>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {wizardData.coverLetter}
              </div>
            </div>
          </ScrollArea>
        </Card>
      )}

      <div className="flex justify-between">
        <Button
          data-testid="prev-step-btn"
          variant="outline"
          size="lg"
          onClick={prevStep}
          className="h-12 px-8 font-semibold"
        >
          <ArrowLeft className="mr-2 w-5 h-5" />
          Back
        </Button>
        <Button
          data-testid="next-step-btn"
          size="lg"
          onClick={nextStep}
          disabled={!generated}
          className="h-12 px-8 font-semibold transition-all active:scale-95"
        >
          Continue to Download
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  );
};

export default Step5CoverLetter;
